import React from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {IController} from '@src/@interfaces/services/ControllersRepository/IController';
import app from '@src/helpers/app';
import {AbstractRouteProps} from '@src/@types/components/Routes';
import ViewRepresentation from '../ViewRepresentation/ViewRepresentation';
import Error403 from '@views/errors/Error403';
import {UserContext} from '@src/contexts/UserContext';

export default abstract class AbstractRoute<P extends AbstractRouteProps = AbstractRouteProps> extends React.Component<P> {
    protected controller: IController | null = null;
    protected onDeniedRedirectRouteName = 'auth@login';
    protected onDeniedFallbackRedirectUrl = '/';

    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    render() {
        if (this.userDetailsNowLoading()) {
            return this.renderLoader();
        }

        if (!this.isAccessGranted()) {
            return this.renderAccessDenied();
        }

        return this.renderAccessGranted();
    }

    protected abstract isAccessGranted(): boolean;

    protected abstract userDetailsNowLoading(): boolean;

    protected permissionsGranted(): boolean {
        const permissions = this.props.permissions;
        if (permissions == null) {
            return true;
        }

        return this.context != null && this.context.canAny(permissions);
    }

    protected renderAccessDenied(): JSX.Element {
        return <ViewRepresentation element={Error403}/>;
    }

    protected renderRedirect(): JSX.Element {
        const loginUrl = app('url').route(this.onDeniedRedirectRouteName);

        return <Navigate
                to={loginUrl ?? this.onDeniedFallbackRedirectUrl}
                replace={true}
                state={{from: {}}}
        />;
    }

    protected renderAccessGranted(): JSX.Element | null {
        const controller = this.props.controller;
        const action = this.props.action;

        return <>
            {
                action ? (
                        <ViewRepresentation
                                element={controller.getActionComponent(action)}
                                viewProps={controller.getActionComponentProps(action)}
                        />
                ) : null
            }

            <Outlet/>
        </>;
    }

    protected renderLoader(): JSX.Element | null {
        return null;
    }
}
